import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';

import { BottomSheet, Drop, useBreakpoint } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import NoCallAnswer from 'src/components/Employer/NoCallAnswer';
import NonAppearance from 'src/components/Employer/NonAppearance';
import RemindModal from 'src/components/InterviewReminder';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import MarkCardAsReadButton from 'src/components/ResumeCard/Actions/MarkCardAsReadButton/Wrapper';
import SaveToFavorites from 'src/components/ResumeCard/Actions/SaveToFavoritesButton';
import ResumeComplaint from 'src/components/ResumeComplaint';
import useResumeComplaint from 'src/components/ResumeComplaint/useResumeComplaint';
import translation from 'src/components/translation';
import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import useOnOffState from 'src/hooks/useOnOffState';
import { useIsZarplataPlatform } from 'src/hooks/usePlatform';
import { useSelector } from 'src/hooks/useSelector';
import {
    CurrentInterview,
    removeInterviewRemind,
    setInterviewRemind,
    setNotAppearanceDate,
} from 'src/models/candidatesList';

import ComplaintButton from 'src/components/ResumeCard/Actions/Additional/Menu/Complaint';
import InviteToVideoCall from 'src/components/ResumeCard/Actions/Additional/Menu/InviteToVideoCall';
import Remind from 'src/components/ResumeCard/Actions/Additional/Menu/Remind';
import ResumeShare from 'src/components/ResumeCard/Actions/Additional/Menu/ResumeShare';
import { AdditionalMenuProps } from 'src/components/ResumeCard/Actions/Additional/Menu/types';

import styles from './styles.less';

const Features = {
    videoCallsDisabled: 'video_calls_disabled',
};

const MenuResponse: TranslatedComponent<AdditionalMenuProps> = ({
    isMenuVisible,
    closeMenu,
    menuDropProps,
    onShareResume,
    isHiddenByPfp,
}) => {
    const dispatch = useDispatch();
    const inEmployerSubstatesExp = useSelector((state) => state.isEmployerSubstatesExp);
    const isHiringManagerExperiment = useSelector((state) => state.isHiringManagerExperiment);
    const isHiringManager = useSelector((state) => state.isHiringManager);
    const isVideoCallsDisabled = useFeatureEnabled(Features.videoCallsDisabled);

    const { isMobile, isGtS, isM, isGtM } = useBreakpoint();

    const { isNew, isResponse, markAsRead, resume, vacancyId, isChangeState } = useContext(ResumeCardActionsContext);
    const { topicId } = resume;

    const isZp = useIsZarplataPlatform();
    const [isRemindModalOpen, openRemindModal, closeRemindModal] = useOnOffState(false);
    const remindInfo = useSelector(
        (state) => state.candidatesList?.topicsNegotiationInterviewInfo.topics?.[topicId || '']
    );
    const [nonAppearance, , hideNonAppearance] = useOnOffState(true);
    const [noAnswer, , hideNoAnswer] = useOnOffState(true);
    const { onOpen: onOpenResumeComplaint, ...resumeComplaintProps } = useResumeComplaint({
        resumeHash: resume._attributes.hash,
        isResponse,
        onOpen: closeMenu,
    });

    const handleChangeRemind = useCallback(
        (currentInterview: CurrentInterview) => {
            dispatch(setInterviewRemind({ currentInterview }));
        },
        [dispatch]
    );

    const handleDeleteRemind = useCallback(() => {
        if (topicId) {
            dispatch(removeInterviewRemind({ id: topicId }));
        }
    }, [dispatch, topicId]);

    const content = (
        <>
            {(isMobile || (inEmployerSubstatesExp && !isGtM && isChangeState)) && (
                <MarkCardAsReadButton closeAdditionalMenu={closeMenu} />
            )}
            {isHiringManagerExperiment && !isHiringManager && onShareResume && (
                <ResumeShare onResumeShare={onShareResume} closeMenu={closeMenu} />
            )}
            {((isM && (isHiringManagerExperiment || isNew)) ||
                (inEmployerSubstatesExp && ((isNew && isGtS) || isM) && isChangeState)) && (
                <SaveToFavorites closeAdditionalMenu={closeMenu} />
            )}
            {!isZp && !isVideoCallsDisabled && <InviteToVideoCall closeMenu={closeMenu} />}
            {remindInfo && topicId && !isHiddenByPfp && (
                <Remind
                    topicId={topicId}
                    remindInfo={remindInfo}
                    openRemindModal={openRemindModal}
                    closeMenu={closeMenu}
                />
            )}
            {nonAppearance && !isHiddenByPfp && (
                <NonAppearance
                    viewType="menu"
                    onSubmit={() => {
                        hideNonAppearance();
                        dispatch(setNotAppearanceDate({ resumeHash: resume._attributes.hash }));
                    }}
                    closeMenu={closeMenu}
                    vacancyId={vacancyId}
                    markAsRead={markAsRead}
                    resumeHash={resume._attributes.hash}
                />
            )}
            {noAnswer && topicId && !isHiddenByPfp && (
                <NoCallAnswer
                    topicId={topicId}
                    viewType="menuAdditional"
                    onClick={() => {
                        hideNoAnswer();
                        closeMenu();
                    }}
                />
            )}
            <ComplaintButton onClick={onOpenResumeComplaint} />
        </>
    );

    return (
        <>
            <Drop visible={isMenuVisible} onClose={closeMenu} {...menuDropProps}>
                <div className={styles.drop}>{content}</div>
            </Drop>
            <BottomSheet visible={isMenuVisible} onClose={closeMenu}>
                {content}
            </BottomSheet>
            <ResumeComplaint {...resumeComplaintProps} />
            {remindInfo && topicId && (
                <RemindModal
                    visible={isRemindModalOpen}
                    topicId={topicId}
                    remindInfo={remindInfo}
                    onClose={closeRemindModal}
                    onChange={handleChangeRemind}
                    onDelete={handleDeleteRemind}
                />
            )}
        </>
    );
};

export default translation(MenuResponse);
